/**
 * ConversationHistoryDrawer — slide-in list of the user's past Ion
 * conversations. Triggered by the History icon in ChatPanelHeader.
 *
 * Fetches fresh on every open (no cached list signal) so the user always
 * sees server truth — soft-delete and rename round-trip immediately.
 *
 * Click an entry → hydrate it into the active store. Trash icon → soft-delete.
 *
 * Mounted inline by IonSurface so the drawer can sit inside the chat surface
 * (docked/rail: covers the panel; full: anchored to the left chat column).
 * State `open` is owned by the parent.
 */

import { useEffect, useState } from 'preact/hooks'
import {
  listConversations,
  loadConversationIntoStore,
  deleteConversation,
} from '../../services/ionConversationService.js'
import {
  currentConversationId,
  clearConversation,
} from '../../state/chatStore.js'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 */
export function ConversationHistoryDrawer({ open, onClose }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const activeId = currentConversationId.value

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    listConversations({ limit: 30 })
      .then((data) => {
        if (cancelled) return
        setItems(data.conversations)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.message || 'network')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  if (!open) return null

  async function handleSelect(id) {
    const result = await loadConversationIntoStore(id)
    if (result.ok) onClose()
    else setError(result.error)
  }

  async function handleDelete(id, e) {
    // Stop the row's onClick from also firing.
    e.stopPropagation()
    try {
      await deleteConversation(id)
      setItems((prev) => prev.filter((c) => c.conversationId !== id))
    } catch (err) {
      setError(err?.message || 'network')
    }
  }

  return (
    <div class="ion-history" role="dialog" aria-label="Conversation history">
      <div class="ion-history__header">
        <h3 class="ion-history__title">History</h3>
        <button
          type="button"
          class="ion-history__close"
          onClick={onClose}
          aria-label="Close history"
        >
          ✕
        </button>
      </div>

      <button
        type="button"
        class="ion-history__new"
        onClick={() => {
          clearConversation()
          onClose()
        }}
      >
        <span aria-hidden="true">＋</span>
        <span>New conversation</span>
      </button>

      {loading && <div class="ion-history__state">Loading…</div>}

      {error && (
        <div class="ion-history__state ion-history__state--error">
          Couldn't load conversations: {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div class="ion-history__state">No previous conversations yet.</div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul class="ion-history__list">
          {items.map((item) => {
            const isActive = item.conversationId === activeId
            return (
              <li
                key={item.conversationId}
                class={
                  'ion-history__item' +
                  (isActive ? ' ion-history__item--active' : '')
                }
              >
                <button
                  type="button"
                  class="ion-history__row"
                  onClick={() => handleSelect(item.conversationId)}
                >
                  <span class="ion-history__row-title">
                    {item.title || 'Untitled conversation'}
                  </span>
                  <span class="ion-history__row-meta">
                    {formatRelativeTime(item.lastActivityAt)} ·{' '}
                    {item.turnCount || 0} turn{item.turnCount === 1 ? '' : 's'}
                  </span>
                </button>
                <button
                  type="button"
                  class="ion-history__delete"
                  onClick={(e) => handleDelete(item.conversationId, e)}
                  aria-label="Delete conversation"
                  title="Delete conversation"
                >
                  ✕
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function formatRelativeTime(iso) {
  if (!iso) return ''
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return ''
  const diffMs = Date.now() - ts
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  if (diffMs < minute) return 'just now'
  if (diffMs < hour) return `${Math.round(diffMs / minute)}m ago`
  if (diffMs < day) return `${Math.round(diffMs / hour)}h ago`
  if (diffMs < 7 * day) return `${Math.round(diffMs / day)}d ago`
  return new Date(ts).toLocaleDateString()
}
